import { Text } from '@/components/atoms/text';
import { Box, ClickAwayListener, LinearProgress, Stack, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { StyledLegend, StyledLegendContainer } from './styles';
import { useMemo, useState } from 'react';
import { Mobile } from '../viewport/mobile';
import { Desktop } from '../viewport/desktop';

interface NoteBarProps {
  note: number | string;
  placement: 'left' | 'right';
  isMobile: boolean;
  color: string;
  height?: number;
  tooltip?: string;
}

const parseLocalizedNumber = (value: number | string): number => {
  if (typeof value === 'number') {
    return value;
  }
  const normalized = String(value).replace(',', '.');
  const parsed = parseFloat(normalized);
  return isNaN(parsed) ? 0 : parsed;
};

export const NoteBar = ({
  note,
  placement,
  isMobile,
  color,
  height = 10,
  tooltip,
}: NoteBarProps) => {
  const theme = useTheme();
  const numericNote = useMemo(() => parseLocalizedNumber(note), [note]);

  const [open, setOpen] = useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        width: '100%',
        alignItems: 'center',
        marginTop: isMobile ? theme.spacing(1.5) : 0,
      }}
    >
      {placement === 'left' && (
        <Box sx={{ width: '100%', minWidth: '40px', maxWidth: 'fit-content' }}>
          <Text variant="subtitle2" color="text.primary" align="justify">
            {note}
          </Text>
        </Box>
      )}

      <Mobile>
        <ClickAwayListener onClickAway={handleTooltipClose}>
          <Tooltip
            disableHoverListener={isMobile}
            disableFocusListener={isMobile}
            disableTouchListener={isMobile}
            open={open}
            onClose={handleTooltipClose}
            title={
              tooltip ? (
                <StyledLegendContainer>
                  <StyledLegend sx={{ backgroundColor: color }} />
                  <Text variant="caption" color="common.white">
                    {tooltip}
                  </Text>
                </StyledLegendContainer>
              ) : (
                ''
              )
            }
            placement="top"
            arrow
          >
            <Box sx={{ width: '100%' }} onClick={handleTooltipOpen}>
              <LinearProgress
                variant="determinate"
                value={numericNote}
                sx={{
                  height,
                  borderRadius: 5,
                  backgroundColor: theme.palette.action.hover,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: color,
                    borderRadius: 5,
                  },
                }}
              />
            </Box>
          </Tooltip>
        </ClickAwayListener>
      </Mobile>

      <Desktop>
        <>
          <Tooltip
            title={
              tooltip ? (
                <StyledLegendContainer>
                  <StyledLegend sx={{ backgroundColor: color }} />
                  <Text variant="caption" color="common.white">
                    {tooltip}
                  </Text>
                </StyledLegendContainer>
              ) : (
                ''
              )
            }
            placement="top"
            arrow
          >
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={numericNote}
                sx={{
                  height,
                  borderRadius: 5,
                  backgroundColor: theme.palette.action.hover,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: color,
                    borderRadius: 5,
                  },
                }}
              />
            </Box>
          </Tooltip>
        </>
      </Desktop>

      {placement === 'right' && (
        <Box sx={{ width: '100%', minWidth: '40px', maxWidth: 'fit-content' }}>
          <Text variant="subtitle2" color="text.primary" align="justify">
            {note}
          </Text>
        </Box>
      )}
    </Stack>
  );
};
